'use client';

import type { ShowDrawer } from '@unifyapps/carbon/components/Drawer';
import { EMPTY_ARRAY } from '@unifyapps/carbon/consts/empty';
import { InterfaceNavigationHelper } from '@unifyapps/carbon/helpers/InterfaceNavigationHelper';
import { NOCODE_NAVIGATION_CONTAINER } from '@unifyapps/carbon/no-code/const';
import { useGlobalAppStateStore } from '@unifyapps/carbon/no-code/stores/app/GlobalAppStateStore';
import {
  type InterfaceEntity,
  type NavItem,
  NavigationItemLabelVariant,
  NavigationPosition,
  type NavigationResolution,
  type PageNavItem,
  type UrlNavItem,
} from '@unifyapps/defs/types/interface';
import { slowCn } from '@unifyapps/ui/lib/utils';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { Children, cloneElement, useCallback, useMemo, useRef } from 'react';
import { useAppNavigationModel } from '../hooks/useAppNavigationModel';
import { usePermissibleNavItems } from './hooks/usePermissibleNavItems';
import { useProfileClick } from './hooks/useProfileClick';
import { useSecondaryCustomizers } from './hooks/useSecondaryCustomizers';

function AppSideOrBottomNavigationController({
  unopenedNotificationsCount,
  interfaceRecord,
  children,
  showDrawer,
  resolution,
  isDrawerOpen,
  hideDrawer,
}: {
  unopenedNotificationsCount?: number;
  interfaceRecord?: InterfaceEntity;
  children: React.ReactNode;
  showDrawer: ShowDrawer;
  isDrawerOpen: boolean;
  resolution: NavigationResolution;
  hideDrawer?: () => void;
}) {
  const isNavigationDrawerOpen = useGlobalAppStateStore().hooks.useIsNavigationDrawerOpen();
  const { setNavigationState } = useGlobalAppStateStore().hooks.useActions();

  const handleExpandedChange = useCallback(
    (expanded: boolean) => {
      setNavigationState({ isDrawerOpen: expanded });
    },
    [setNavigationState],
  );

  const sideNavRef = useRef<HTMLDivElement | null>(null);
  const navigationContainerRef = useRef<HTMLDivElement | null>(null);

  const { buildNavigation, app, user, permissions, navigationParams } = useAppNavigationModel({
    interfaceRecord,
    resolution,
    unopenedNotificationsCount,
  });

  const onProfileClick = useProfileClick({ interfaceRecord, loggedInUserId: user.id });

  const permissibleUserMenuItems = usePermissibleNavItems({
    items: interfaceRecord
      ? InterfaceNavigationHelper.getUserMenuItemsByResolution(interfaceRecord, resolution)
      : (EMPTY_ARRAY as NavItem[]),
    permissions,
    pagePermissions: interfaceRecord?.properties.pagePermissions,
  }) as (UrlNavItem | PageNavItem)[] | undefined;

  const { borderProps, navigationClassNames, navigationProps, navigationStyles } = navigationParams;
  const { theme } = navigationProps;
  const isTopPositioned = navigationProps.position === NavigationPosition.Top;

  const customizers = useSecondaryCustomizers({
    interfaceRecord,
    permissibleUserMenuItems,
    hideDrawer,
    onProfileClick,
    theme,
    isTopPositioned,
    unopenedNotificationsCount,
    showDrawer,
    resolution,
    sideNavRef,
    navigationContainerRef,
    borderProps,
  });

  const navigation = useMemo(() => buildNavigation(customizers), [buildNavigation, customizers]);

  const { className, containerClassName, heightClassName } = navigationClassNames;

  const adaptedNavigationClassNames = useMemo(() => {
    return {
      ...navigationClassNames,
      containerClassName: slowCn('shrink-0 print:hidden', containerClassName, {
        'basis-sidebarWidth':
          navigationProps.navigationItemAppearance?.labelVariant ===
          NavigationItemLabelVariant.Expandable,
      }),
      className: slowCn(
        'workspace-side-nav',
        {
          '!shadow-none': isDrawerOpen,
        },
        className,
        heightClassName,
      ),
    };
  }, [
    className,
    containerClassName,
    heightClassName,
    isDrawerOpen,
    navigationClassNames,
    navigationProps.navigationItemAppearance?.labelVariant,
  ]);

  if (!navigation) return null;

  return Children.map(children, (child) => {
    return cloneElement(child as ReactElement<Record<string, unknown>>, {
      navigationProps: {
        navigation,
        app,
        ...navigationProps,
      },
      navigationClassNames: adaptedNavigationClassNames,
      navigationStyles,
      scheme: theme,
      sideNavRef,
      navigationContainerRef,
      expanded: isNavigationDrawerOpen,
      onExpandedChange: handleExpandedChange,
      className: clsx('navigation-container-matrix', NOCODE_NAVIGATION_CONTAINER),
    });
  });
}

export default AppSideOrBottomNavigationController;
